import { cn } from "@lib/cn"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import type { ReactNode } from "react"

export interface TableColumn<T> {
  key: string
  header: string
  cell: (row: T) => ReactNode
  /** Numbers and dates read better right-aligned. */
  align?: "end"
  /** The server's sort key; the header becomes a sort button. */
  sortKey?: string
}

interface Props<T> {
  /** Names the table for a screen reader ("Profesores de BetaApp"). Visually hidden. */
  caption: string
  rows: T[]
  rowKey: (row: T) => string
  /**
   * The cell that says WHO or WHAT the row is. A `<th scope="row">`, so a
   * screen reader announces it with every other cell of the row, and the
   * title of the card the row becomes below `md`.
   */
  identity: { header: string; cell: (row: T) => ReactNode; sortKey?: string }
  columns: TableColumn<T>[]
  /** Row actions (see `RowActions`). The column is omitted when absent. */
  actions?: (row: T) => ReactNode
  actionsHeader?: string
  /** True while the rows on screen are the previous query's. Dims the list
   *  and marks it `aria-busy` until the new ones land. */
  stale?: boolean
  /** The active sort (PRD-56 §FR-19) and what a header click asks for. */
  sort?: { key: string; desc: boolean }
  onSort?: (key: string) => void
  /**
   * Below `md`: `stacked` (label over value) or `inline` — name and actions
   * on one row, the other cells on one line under it. People lists use
   * `inline`: fifty teachers of label/value grids were twelve thousand
   * pixels of scrolling on a phone.
   */
  cards?: "stacked" | "inline"
}

/** A header that sorts: `aria-sort` on the cell, a real button inside. */
function HeaderCell({
  label,
  sortKey,
  sort,
  onSort,
  align,
}: {
  label: string
  sortKey?: string
  sort?: { key: string; desc: boolean }
  onSort?: (key: string) => void
  align?: "end"
}) {
  const active = !!sortKey && sort?.key === sortKey
  const Icon = active ? (sort?.desc ? ArrowDown : ArrowUp) : ArrowUpDown
  return (
    <th
      scope="col"
      aria-sort={active ? (sort?.desc ? "descending" : "ascending") : undefined}
      className={cn(
        "text-label px-4 py-2.5 font-medium whitespace-nowrap",
        align === "end" ? "text-right" : "text-left",
      )}
    >
      {sortKey && onSort ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            "hover:text-ink -mx-1 inline-flex items-center gap-1 rounded px-1 transition-colors duration-150",
            active && "text-ink",
          )}
        >
          {label}
          <Icon
            className={cn("h-3.5 w-3.5", active ? "text-ink" : "text-ink-subtle")}
            aria-hidden
          />
        </button>
      ) : (
        label
      )}
    </th>
  )
}

/**
 * The console's one table (PRD-60 chunk B). Four hand-rolled tables each had
 * an `overflow-x-auto` wrapper with no hint that it scrolled, so on a phone
 * the status and the actions sat off-screen; uppercase slate headers, no
 * `scope`, and a different radius from every card around them.
 *
 * From `md` up it is a table. Below `md` the same rows become a list of
 * cards: identity and actions first, the other columns as label/value
 * pairs underneath — nothing is behind a horizontal scroll.
 */
export function Table<T>({
  caption,
  rows,
  rowKey,
  identity,
  columns,
  actions,
  actionsHeader,
  stale,
  sort,
  onSort,
  cards = "stacked",
}: Props<T>) {
  return (
    <div
      aria-busy={stale || undefined}
      className={cn("transition-opacity duration-150", stale && "opacity-60")}
    >
      {/* `overflow-x-auto`, not `hidden`: inside a narrow container (the
          wizard's card) a wide row used to clip its "⋯" out of existence.
          Scrolling is the last resort; hosts should give tables room. */}
      <div className="rounded-card border-border bg-surface hidden overflow-x-auto border md:block">
        <table className="w-full text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-inset">
            <tr>
              <HeaderCell
                label={identity.header}
                sortKey={identity.sortKey}
                sort={sort}
                onSort={onSort}
              />
              {columns.map((col) => (
                <HeaderCell
                  key={col.key}
                  label={col.header}
                  sortKey={col.sortKey}
                  sort={sort}
                  onSort={onSort}
                  align={col.align}
                />
              ))}
              {actions && (
                <th scope="col" className="px-4 py-2.5">
                  <span className="sr-only">{actionsHeader}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-hairline divide-y">
            {rows.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-inset/50 transition-colors duration-150">
                <th scope="row" className="text-ink px-4 py-3 text-left align-middle font-normal">
                  {identity.cell(row)}
                </th>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "text-ink-muted px-4 py-3 align-middle",
                      col.align === "end" && "text-right tabular-nums",
                    )}
                  >
                    {col.cell(row)}
                  </td>
                ))}
                {actions && (
                  <td className="px-4 py-3 text-right align-middle whitespace-nowrap">
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul aria-label={caption} className="space-y-3 md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)} className="rounded-card border-border bg-surface border px-4 py-3">
            {columns.length === 0 ? (
              // Nothing but a name (a subject): one line, not a card with an
              // empty body and a divider.
              <div className="flex items-center justify-between gap-3">
                <div className="text-ink min-w-0">{identity.cell(row)}</div>
                {actions?.(row)}
              </div>
            ) : cards === "inline" ? (
              // The name on its own line; the other cells, without labels,
              // and the actions share the next one and wrap when they must.
              <>
                <div className="text-ink min-w-0">{identity.cell(row)}</div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <div className="text-ink-muted flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    {columns.map((col) => (
                      <div key={col.key} className="min-w-0">
                        {col.cell(row)}
                      </div>
                    ))}
                  </div>
                  {actions && <div className="-mr-2 ml-auto">{actions(row)}</div>}
                </div>
              </>
            ) : (
              <>
                <div className="text-ink min-w-0">{identity.cell(row)}</div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                  {columns.map((col) => (
                    <div key={col.key} className="min-w-0">
                      <dt className="text-label">{col.header}</dt>
                      <dd className="text-ink-muted mt-0.5 text-sm break-words">{col.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
                {actions && (
                  <div className="border-hairline mt-3 flex justify-end border-t pt-3">
                    {actions(row)}
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
